<template>
  <modal id="pushConfigurationsForm"
         v-model="visible"
         :title="id ? $t('a-pushConfigurationsForm:updatePushConfiguration') : $t('a-pushConfigurationsForm:createPushConfiguration')"
         no-footer
         @modal:cancel="cancel"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('a-pushConfigurationsForm:required') | capitalize }}</small>

    <div slot="modal-body">
      <!-- Fake input to prevent saved password to be filled -->
      <input id="fakeEmail" type="email" class="d-none">
      <input id="fakePassword" type="password" class="d-none">

      <stepper ref="stepper" v-model="step" :steps="steps" :on-next="next" use-key />

      <template v-if="step === 'identity'">
        <div v-if="isRole.superAdmin" class="form-group">
          <label for="customer" class="control-label">{{ $t('a-pushConfigurationsForm:customer') | capitalize }} *</label>
          <select id="customer"
                  v-model="customer"
                  v-validate="{required: isRole.superAdmin}"
                  :data-vv-as="$t('a-pushConfigurationsForm:customer')"
                  :class="{ 'is-invalid': errors.has('customer') }"
                  name="customer"
                  class="form-control"
          >
            <option v-for="c in $store.getters.customers" :key="c.id" :value="c" :data-customer-id="c.id">{{ c.name }}</option>
          </select>
          <div class="invalid-feedback">{{ errors.first('customer') }}</div>
        </div>

        <div class="form-group">
          <label for="name" class="control-label">{{ $t('a-pushConfigurationsForm:name') | capitalize }} *</label>
          <input id="name"
                 key="name"
                 v-model="name"
                 v-validate="'required|text'"
                 :data-vv-as="$t('a-pushConfigurationsForm:name')"
                 :class="{ 'is-invalid': errors.has('name') }"
                 name="name"
                 type="text"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('name') }}</div>
        </div>

        <div class="form-group">
          <label for="type" class="control-label d-block">{{ $t('a-pushConfigurationsForm:type') | capitalize }} *</label>
          <input id="typeHttp"
                 v-model="type"
                 v-validate="'required'"
                 :data-vv-as="$t('a-pushConfigurationsForm:type')"
                 :class="{ 'is-invalid': errors.has('type') }"
                 class="form-control"
                 type="radio"
                 value="HTTP"
                 name="type"
          >
          <label for="typeHttp" class="control-label">{{ $t('a-pushConfigurationsForm:typeHttp') | capitalize }}</label>

          <input id="typeWebsocket"
                 v-model="type"
                 :class="{ 'is-invalid': errors.has('type') }"
                 class="form-control"
                 type="radio"
                 value="WEBSOCKET"
                 name="type"
          >
          <label for="typeWebsocket" class="control-label">{{ $t('a-pushConfigurationsForm:typeWebsocket') | capitalize }}</label>

          <input id="typeMqtt"
                 v-model="type"
                 :class="{ 'is-invalid': errors.has('type') }"
                 class="form-control"
                 type="radio"
                 value="MQTT"
                 name="type"
          >
          <label for="typeMqtt" class="control-label">{{ $t('a-pushConfigurationsForm:typeMqtt') | capitalize }}</label>

          <div class="invalid-feedback">{{ errors.first('type') }}</div>
        </div>

        <div class="form-group">
          <label for="msgDetailLevel" class="control-label d-block">{{ $t('a-pushConfigurationsForm:msgDetailLevel') | capitalize }} *</label>
          <input id="payload"
                 v-model="msgDetailLevel"
                 v-validate="'required'"
                 :data-vv-as="$t('a-pushConfigurationsForm:msgDetailLevel')"
                 :class="{ 'is-invalid': errors.has('msgDetailLevel') }"
                 class="form-control"
                 type="radio"
                 value="PAYLOAD"
                 name="msgDetailLevel"
          >
          <label for="payload" class="control-label">{{ $t('a-pushConfigurationsForm:payload') | capitalize }}</label>

          <input id="radio"
                 v-model="msgDetailLevel"
                 class="form-control"
                 type="radio"
                 value="RADIO"
                 name="msgDetailLevel"
          >
          <label for="radio" class="control-label">{{ $t('a-pushConfigurationsForm:radio') | capitalize }}</label>

          <input id="network"
                 v-model="msgDetailLevel"
                 class="form-control"
                 type="radio"
                 value="NETWORK"
                 name="msgDetailLevel"
          >
          <label for="network" class="control-label">{{ $t('a-pushConfigurationsForm:network') | capitalize }}</label>

          <div class="invalid-feedback">{{ errors.first('msgDetailLevel') }}</div>
        </div>
      </template>

      <template v-if="step === 'connection' && (type === 'HTTP' || type === 'WEBSOCKET')">
        <h3>{{ $t('a-pushConfigurationsForm:connection') | capitalize }}</h3>

        <div class="form-row">
          <div class="form-group col-lg-12">
            <label for="url" class="control-label">{{ $t('a-pushConfigurationsForm:url') | capitalize }} *</label>
            <input id="url"
                   v-model="url"
                   v-validate="{rules: {
                     required: true,
                     text: true,
                     http: type === 'HTTP',
                     websocket: type === 'WEBSOCKET'
                   }}"
                   :data-vv-as="$t('a-pushConfigurationsForm:url')"
                   :class="{ 'is-invalid': errors.has('url') }"
                   type="text"
                   name="url"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('url') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-6">
            <label for="user" class="control-label">{{ $t('a-pushConfigurationsForm:user') | capitalize }}<span v-if="user || password"> *</span></label>
            <input id="user"
                   v-model="user"
                   v-validate="{rules: {
                     required: password!==null && password!=='',
                     text: true
                   }}"
                   :data-vv-as="$t('a-pushConfigurationsForm:user')"
                   :class="{ 'is-invalid': errors.has('user') }"
                   type="text"
                   name="user"
                   class="form-control"
                   autocomplete="off"
            >
            <div class="invalid-feedback">{{ errors.first('user') }}</div>
          </div>

          <div class="form-group col-lg-6">
            <label for="password" class="control-label">{{ $t('a-pushConfigurationsForm:password') | capitalize }}<span v-if="password || (user!==null && user!=='' && !id)"> *</span></label>
            <input id="password"
                   v-model="password"
                   v-validate="{rules: {
                     required: user!==null && user!=='' && !id,
                     text: true
                   }}"
                   :data-vv-as="$t('a-pushConfigurationsForm:password')"
                   :class="{ 'is-invalid': errors.has('password') }"
                   type="password"
                   name="password"
                   class="form-control"
                   autocomplete="off"
            >
            <div class="invalid-feedback">{{ errors.first('password') }}</div>
          </div>
        </div>

        <div v-if="type === 'HTTP'" class="form-row">
          <div class="form-group col-lg-6">
            <label for="httpDataUpRoute" class="control-label">{{ $t('a-pushConfigurationsForm:httpDataUpRoute') | capitalize }}</label>
            <input id="httpDataUpRoute"
                   v-model="httpDataUpRoute"
                   v-validate="'text'"
                   :data-vv-as="$t('a-pushConfigurationsForm:httpDataUpRoute')"
                   :class="{ 'is-invalid': errors.has('httpDataUpRoute') }"
                   placeholder="/dataUp"
                   type="text"
                   name="httpDataUpRoute"
                   class="form-control"
                   autocomplete="off"
            >
            <div class="invalid-feedback">{{ errors.first('httpDataUpRoute') }}</div>
          </div>

          <div class="form-group col-lg-6">
            <label for="httpDataDownEventRoute" class="control-label">{{ $t('a-pushConfigurationsForm:httpDataDownEventRoute') | capitalize }}</label>
            <input id="httpDataDownEventRoute"
                   v-model="httpDataDownEventRoute"
                   v-validate="'text'"
                   :data-vv-as="$t('a-pushConfigurationsForm:httpDataDownEventRoute')"
                   :class="{ 'is-invalid': errors.has('httpDataDownEventRoute') }"
                   placeholder="/dataDownEvent"
                   type="httpDataDownEventRoute"
                   name="httpDataDownEventRoute"
                   class="form-control"
                   autocomplete="off"
            >
            <div class="invalid-feedback">{{ errors.first('httpDataDownEventRoute') }}</div>
          </div>
        </div>
      </template>

      <template v-if="step === 'customHeaders'">
        <div class="form-row">
          <h3 class="col">{{ $t('a-pushConfigurationsForm:customHeaders') | capitalize }}</h3>
          <div class="col-1 text-center mt-1"><i class="icon-add" @click="addEmptyHeader" /></div>
        </div>

        <div v-for="(header, i) in headers" :key="i" class="form-row">
          <div class="form-group col">
            <input :id="`header${i}Key`"
                   v-model="header.key"
                   v-validate="{rules: {
                     required: header.value!==null && header.value!=='',
                     text: true
                   }}"
                   :placeholder="$t('a-pushConfigurationsForm:headerKey')"
                   :data-vv-as="$t('a-pushConfigurationsForm:headerKey')"
                   :class="{ 'is-invalid': errors.has(`header${i}Key`) }"
                   :name="`header${i}Key`"
                   type="text"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first(`header${i}Key`) }}</div>
          </div>

          <div class="form-group col">
            <input :id="`header${i}Value`"
                   v-model="header.value"
                   v-validate="{rules: {
                     required: header.key!==null && header.key!=='',
                     text: true
                   }}"
                   :placeholder="$t('a-pushConfigurationsForm:headerValue')"
                   :data-vv-as="$t('a-pushConfigurationsForm:headerValue')"
                   :class="{ 'is-invalid': errors.has(`header${i}Value`) }"
                   :name="`header${i}Value`"
                   type="text"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first(`header${i}Value`) }}</div>
          </div>

          <div class="col-1 text-center mt-2">
            <i class="icon-remove" @click="removeHeader(i)" />
          </div>
        </div>
      </template>

      <template v-if="step === 'connection' && type === 'MQTT'">
        <h3>{{ $t('a-pushConfigurationsForm:connection') | capitalize }}</h3>

        <div class="form-row">
          <div class="form-group col-lg-6">
            <label for="mqttHost" class="control-label">{{ $t('a-pushConfigurationsForm:mqttHost') | capitalize }} *</label>
            <input id="mqttHost"
                   v-model="mqttHost"
                   v-validate="'required|ipHostname'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttHost')"
                   :class="{ 'is-invalid': errors.has('mqttHost') }"
                   type="text"
                   name="mqttHost"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('mqttHost') }}</div>
          </div>

          <div class="form-group col-lg-6">
            <label for="mqttPort" class="control-label">{{ $t('a-pushConfigurationsForm:mqttPort') | capitalize }} *</label>
            <input id="mqttPort"
                   v-model="mqttPort"
                   v-validate="'required|number|between:1,65535'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttPort')"
                   :class="{ 'is-invalid': errors.has('mqttPort') }"
                   type="mqttPort"
                   name="mqttPort"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('mqttPort') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-12">
            <label for="mqttClientId" class="control-label">{{ $t('a-pushConfigurationsForm:mqttClientId') | capitalize }}</label>
            <input id="mqttClientId"
                   v-model="mqttClientId"
                   v-validate="'text'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttClientId')"
                   :placeholder="$t('a-pushConfigurationsForm:mqttClientIdPlaceholder')"
                   :class="{ 'is-invalid': errors.has('mqttClientId') }"
                   type="text"
                   name="mqttClientId"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('mqttClientId') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-6">
            <label for="mqttConnectTimeout" class="control-label">{{ $t('a-pushConfigurationsForm:mqttConnectTimeout') | capitalize }}</label>
            <input id="mqttConnectTimeout"
                   v-model="mqttConnectTimeout"
                   v-validate="'number|min_value:1'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttConnectTimeout')"
                   :class="{ 'is-invalid': errors.has('mqttConnectTimeout') }"
                   type="text"
                   name="mqttConnectTimeout"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('mqttConnectTimeout') }}</div>
            <small class="form-text text-muted">{{ $t('a-pushConfigurationsForm:mqttConnectTimeoutHelper') | capitalize }}</small>
          </div>

          <div class="form-group col-lg-6">
            <label for="mqttKeepAlive" class="control-label">{{ $t('a-pushConfigurationsForm:mqttKeepAlive') | capitalize }}</label>
            <input id="mqttKeepAlive"
                   v-model="mqttKeepAlive"
                   v-validate="'number|min_value:1'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttKeepAlive')"
                   :class="{ 'is-invalid': errors.has('mqttKeepAlive') }"
                   type="mqttKeepAlive"
                   name="mqttKeepAlive"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('mqttKeepAlive') }}</div>
            <small class="form-text text-muted">{{ $t('a-pushConfigurationsForm:mqttKeepAliveHelper') | capitalize }}</small>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-6">
            <label class="control-label d-block">{{ $t('a-pushConfigurationsForm:mqttTlsEnabled') | capitalize }}</label>
            <input id="mqttTlsEnabled"
                   v-model="mqttTlsEnabled"
                   type="checkbox"
                   name="mqttTlsEnabled"
            >
            <label for="mqttTlsEnabled" />
            <label for="mqttTlsEnabled" class="control-label">{{ $t('a-pushConfigurationsForm:mqttTlsEnabledDescription') | capitalize }}</label>
          </div>

          <div class="form-group col-lg-6">
            <label class="control-label d-block">{{ $t('a-pushConfigurationsForm:mqttCleanSession') | capitalize }}</label>
            <input id="mqttCleanSession"
                   v-model="mqttCleanSession"
                   type="checkbox"
                   name="mqttCleanSession"
            >
            <label for="mqttCleanSession" />
            <label for="mqttCleanSession" class="control-label">{{ $t('a-pushConfigurationsForm:mqttCleanSessionDescription') | capitalize }}</label>
          </div>
        </div>
      </template>

      <template v-if="step === 'security'">
        <h3>{{ $t('a-pushConfigurationsForm:security') | capitalize }}</h3>

        <div class="form-row">
          <div class="form-group col-lg-6">
            <label for="mqttUser" class="control-label">{{ $t('a-pushConfigurationsForm:mqttUser') | capitalize }}</label>
            <input id="mqttUser"
                   v-model="mqttUser"
                   v-validate="'text'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttUser')"
                   :class="{ 'is-invalid': errors.has('mqttUser') }"
                   type="text"
                   name="mqttUser"
                   class="form-control"
                   autocomplete="off"
            >
            <div class="invalid-feedback">{{ errors.first('mqttUser') }}</div>
          </div>

          <div class="form-group col-lg-6">
            <label for="mqttPassword" class="control-label">{{ $t('a-pushConfigurationsForm:mqttPassword') | capitalize }}</label>
            <input id="mqttPassword"
                   v-model="mqttPassword"
                   v-validate="'text'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttPassword')"
                   :class="{ 'is-invalid': errors.has('mqttPassword') }"
                   type="password"
                   name="mqttPassword"
                   class="form-control"
                   autocomplete="off"
            >
            <div class="invalid-feedback">{{ errors.first('mqttPassword') }}</div>
          </div>
        </div>
      </template>

      <template v-if="step === 'topics'">
        <h3>{{ $t('a-pushConfigurationsForm:topics') | capitalize }}</h3>

        <div class="form-row">
          <div class="form-group col-lg-12">
            <label for="mqttDataUpTopic" class="control-label">{{ $t('a-pushConfigurationsForm:mqttDataUpTopic') | capitalize }}</label>
            <input id="mqttDataUpTopic"
                   v-model="mqttDataUpTopic"
                   v-validate="'text'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttDataUpTopic')"
                   :class="{ 'is-invalid': errors.has('mqttDataUpTopic') }"
                   placeholder="/dataUp"
                   type="text"
                   name="mqttDataUpTopic"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('mqttDataUpTopic') }}</div>
            <small class="form-text text-muted">{{ $t('a-pushConfigurationsForm:mqttDataUpTopicHelper') | capitalize }}</small>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-12">
            <label for="mqttDataDownEventTopic" class="control-label">{{ $t('a-pushConfigurationsForm:mqttDataDownEventTopic') | capitalize }}</label>
            <input id="mqttDataDownEventTopic"
                   v-model="mqttDataDownEventTopic"
                   v-validate="'text'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttDataDownEventTopic')"
                   :class="{ 'is-invalid': errors.has('mqttDataDownEventTopic') }"
                   placeholder="/dataDownEvent"
                   type="text"
                   name="mqttDataDownEventTopic"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('mqttDataDownEventTopic') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-4">
            <label for="mqttQoS" class="control-label">{{ $t('a-pushConfigurationsForm:mqttQoS') | capitalize }} *</label>
            <select id="mqttQoS"
                    v-model="mqttQoS"
                    v-validate="'required'"
                    :data-vv-as="$t('a-pushConfigurationsForm:mqttQoS')"
                    :class="{ 'is-invalid': errors.has('mqttQoS') }"
                    name="mqttQoS"
                    class="form-control"
            >
              <option :value="0">0</option>
              <option :value="1">1</option>
              <option :value="2">2</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('mqttQoS') }}</div>
          </div>
        </div>
      </template>

      <template v-if="step === 'messages'">
        <h3>{{ $t('a-pushConfigurationsForm:messages') | capitalize }}</h3>

        <div class="form-row">
          <div class="form-group col-lg-12">
            <label for="mqttWillTopic" class="control-label">{{ $t('a-pushConfigurationsForm:mqttWillTopic') | capitalize }}</label>
            <input id="mqttWillTopic"
                   v-model="mqttWillTopic"
                   v-validate="'text'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttWillTopic')"
                   :class="{ 'is-invalid': errors.has('mqttWillTopic') }"
                   type="text"
                   name="mqttWillTopic"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('mqttWillTopic') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-12">
            <label for="mqttWillPayload" class="control-label">{{ $t('a-pushConfigurationsForm:mqttWillPayload') | capitalize }}</label>
            <input id="mqttWillPayload"
                   v-model="mqttWillPayload"
                   v-validate="'text'"
                   :data-vv-as="$t('a-pushConfigurationsForm:mqttWillPayload')"
                   :class="{ 'is-invalid': errors.has('mqttWillPayload') }"
                   type="text"
                   name="mqttWillPayload"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('mqttWillPayload') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-4">
            <label for="mqttWillQoS" class="control-label">{{ $t('a-pushConfigurationsForm:mqttWillQoS') | capitalize }}</label>
            <select id="mqttWillQoS"
                    v-model="mqttWillQoS"
                    v-validate="'required'"
                    :data-vv-as="$t('a-pushConfigurationsForm:mqttWillQoS')"
                    :class="{ 'is-invalid': errors.has('mqttWillQoS') }"
                    name="mqttWillQoS"
                    class="form-control"
            >
              <option value="" />
              <option :value="0">0</option>
              <option :value="1">1</option>
              <option :value="2">2</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('mqttWillQoS') }}</div>
          </div>
        </div>
      </template>

      <template v-if="step === 'SSL'">
        <h3>{{ $t('a-pushConfigurationsForm:SSL') | capitalize }}</h3>

        <div v-for="file in ['tlsCertFile', 'tlsKeyFile', 'tlsCaFile']" :key="file" class="form-row align-items-end">
          <div class="form-group col-lg-6">
            <label :for="file" class="control-label">{{ $t('a-pushConfigurationsForm:' + file) | capitalize }}</label>
            <input :id="file"
                   :ref="file"
                   :name="file"
                   :data-vv-as="$t('a-pushConfigurationsForm:' + file)"
                   type="file"
                   class="d-none"
                   @change="onFileChange($event.target.name, $event.target.files)"
            >
            <button :id="'choose' + file + 'Button'" class="btn btn-light d-block w-100" @click.prevent="chooseFile(file)">{{ $t('a-pushConfigurationsForm:chooseFile') }}</button>
          </div>
          <div class="form-group col-lg-6 py-1 pl-4">
            <label v-if="file === 'tlsCertFile'" class="control-label mt-1">{{ tlsCertFileName }}</label>
            <label v-if="file === 'tlsKeyFile'" class="control-label mt-1">{{ tlsKeyFileName }}</label>
            <label v-if="file === 'tlsCaFile'" class="control-label mt-1">{{ tlsCaFileName }}</label>
          </div>
        </div>

        <div class="text-muted">{{ $t('a-pushConfigurationsForm:sslHelper') | capitalize }}</div>
      </template>
    </div>

    <div slot="modal-footer" class="w-100 text-right">
      <button v-if="stepIndex > 0" id="stepperPrevious" class="btn btn-flat float-left" @click="$refs.stepper.previous()">{{ $t('stepper:previous') | capitalize }}</button>
      <button id="stepperCancel" class="btn btn-flat" @click="cancel">{{ $t('stepper:cancel') | capitalize }}</button>
      <button v-if="steps.length === 1 || stepIndex < steps.length-1" id="stepperNext" class="btn btn-flat" @click="$refs.stepper.next()">{{ $t('stepper:next') | capitalize }}</button>
      <button v-if="steps.length > 1 && stepIndex === steps.length-1" id="stepperValidate" class="btn btn-flat" @click="processForm()">{{ $t('stepper:validate') | capitalize }}</button>
    </div>
  </modal>
</template>

<script>
import { isNumber, findIndex, isArray, remove, pick, each, find } from 'lodash-es'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

// Define steps as constants in component
const defaultSteps = [ { key: 'identity' } ]
const httpWsSteps = [ { key: 'identity' }, { key: 'connection' }, { key: 'customHeaders' } ]
const httpWsSslSteps = [ { key: 'identity' }, { key: 'connection' }, { key: 'SSL' }, { key: 'customHeaders' } ]
const mqttSteps = [ { key: 'identity' }, { key: 'connection' }, { key: 'security' }, { key: 'topics' }, { key: 'messages' } ]
const mqttSslSteps = [ { key: 'identity' }, { key: 'connection' }, { key: 'security' }, { key: 'SSL' }, { key: 'topics' }, { key: 'messages' } ]

export default {
  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    },

    pushConfigurationToEdit: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,
      // Form
      id: '',
      name: '',
      customer: null,
      type: '',
      msgDetailLevel: '',
      // HTTP / WS
      url: '',
      user: '',
      password: '',
      httpDataUpRoute: '',
      httpDataDownEventRoute: '',
      // SSL
      tlsCertFile: null,
      tlsCertFileName: null,
      tlsKeyFile: null,
      tlsKeyFileName: null,
      tlsCaFile: null,
      tlsCaFileName: null,
      headers: [],
      // MQTT
      mqttHost: '',
      mqttPort: 1883,
      mqttTlsEnabled: false,
      mqttClientId: '',
      mqttConnectTimeout: 30,
      mqttKeepAlive: 30,
      mqttCleanSession: true,
      mqttUser: '',
      mqttPassword: '',
      mqttDataUpTopic: '',
      mqttDataDownEventTopic: '',
      mqttQoS: 0,
      mqttWillTopic: '',
      mqttWillPayload: '',
      mqttWillQoS: '',
      // Stepper
      step: 'identity',
      steps: defaultSteps
    }
  },

  computed: {
    stepIndex () {
      if (isNumber(this.step)) return this.step
      return findIndex(this.steps, step => step.key === this.step)
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    async show () {
      this.reset()
      this.visible = this.show

      if (this.show && this.isRole.superAdmin) {
        if (this.headers.length === 0) this.addEmptyHeader()

        // Get customers
        await this.$store.dispatch('getCustomers')
        if (this.id) this.customer = find(this.$store.getters.customers, customer => customer.id === this.pushConfigurationToEdit.customer.id)
      }
    },
    visible () {
      this.$emit('change', this.visible)
    },

    // Add steps according to configuration
    type () {
      if (this.type === 'MQTT') {
        this.steps = mqttSteps
      } else if (this.type === 'HTTP' || this.type === 'WEBSOCKET') {
        this.steps = httpWsSteps
      }
    },

    // Add SSL steps if url starts by https or wss
    url () {
      if ((this.type === 'HTTP' || this.type === 'WEBSOCKET') && this.url && (this.url.indexOf('https://') !== -1 || this.url.indexOf('wss://') !== -1)) {
        this.steps = httpWsSslSteps
      } else if ((this.type === 'HTTP' || this.type === 'WEBSOCKET') && this.url && (this.url.indexOf('http://') !== -1 || this.url.indexOf('ws://') !== -1)) {
        this.steps = httpWsSteps
      }
    },

    // Add SSL steps if TLS is enabled for MQTT
    mqttTlsEnabled () {
      if (this.type === 'MQTT' && this.mqttTlsEnabled) {
        this.steps = mqttSslSteps
      } else if (this.type === 'MQTT' && !this.mqttTlsEnabled) {
        this.steps = mqttSteps
      }
    },

    pushConfigurationToEdit () {
      if (this.pushConfigurationToEdit) {
        this.id = this.pushConfigurationToEdit.id
        this.name = this.pushConfigurationToEdit.name
        this.type = this.pushConfigurationToEdit.type
        this.msgDetailLevel = this.pushConfigurationToEdit.msgDetailLevel
        // HTTP / WS
        this.url = this.pushConfigurationToEdit.url
        this.user = this.pushConfigurationToEdit.user
        this.httpDataUpRoute = this.pushConfigurationToEdit.httpDataUpRoute
        this.httpDataDownEventRoute = this.pushConfigurationToEdit.httpDataDownEventRoute
        this.headers = this.pushConfigurationToEdit.headers || []
        if (this.headers.length === 0) this.addEmptyHeader()
        // SSL
        this.tlsCertFileName = this.pushConfigurationToEdit.tlsCertFileName
        this.tlsKeyFileName = this.pushConfigurationToEdit.tlsKeyFileName
        this.tlsCaFileName = this.pushConfigurationToEdit.tlsCaFileName
        // MQTT
        this.mqttHost = this.pushConfigurationToEdit.mqttHost
        this.mqttPort = this.pushConfigurationToEdit.mqttPort
        this.mqttTlsEnabled = this.pushConfigurationToEdit.mqttTlsEnabled
        this.mqttClientId = this.pushConfigurationToEdit.mqttClientId
        this.mqttConnectTimeout = this.pushConfigurationToEdit.mqttConnectTimeout
        this.mqttKeepAlive = this.pushConfigurationToEdit.mqttKeepAlive
        this.mqttCleanSession = this.pushConfigurationToEdit.mqttCleanSession
        this.mqttUser = this.pushConfigurationToEdit.mqttUser
        this.mqttDataUpTopic = this.pushConfigurationToEdit.mqttDataUpTopic
        this.mqttDataDownEventTopic = this.pushConfigurationToEdit.mqttDataDownEventTopic
        this.mqttQoS = this.pushConfigurationToEdit.mqttQoS
        this.mqttWillTopic = this.pushConfigurationToEdit.mqttWillTopic
        this.mqttWillPayload = this.pushConfigurationToEdit.mqttWillPayload
        this.mqttWillQoS = this.pushConfigurationToEdit.mqttWillQoS
      }
    }
  },

  created () {
    // Add translated labels to steps
    const translateLabel = step => { step.label = this.$t('a-pushConfigurationsForm:' + step.key) }
    defaultSteps.map(translateLabel)
    httpWsSteps.map(translateLabel)
    httpWsSslSteps.map(translateLabel)
    mqttSteps.map(translateLabel)
    mqttSslSteps.map(translateLabel)
  },

  methods: {
    async reset () {
      this.id = ''
      this.name = ''
      this.customer = null
      this.type = ''
      this.msgDetailLevel = ''
      // HTTP / WS
      this.url = ''
      this.user = ''
      this.password = ''
      this.httpDataUpRoute = ''
      this.httpDataDownEventRoute = ''
      this.headers = []
      // SSL
      this.tlsCertFile = null
      this.tlsCertFileName = null
      this.tlsKeyFile = null
      this.tlsKeyFileName = null
      this.tlsCaFile = null
      this.tlsCaFileName = null
      // MQTT
      this.mqttHost = ''
      this.mqttPort = 1883
      this.mqttTlsEnabled = false
      this.mqttClientId = ''
      this.mqttConnectTimeout = 30
      this.mqttKeepAlive = 30
      this.mqttCleanSession = true
      this.mqttUser = ''
      this.mqttPassword = ''
      this.mqttDataUpTopic = ''
      this.mqttDataDownEventTopic = ''
      this.mqttQoS = 0
      this.mqttWillTopic = ''
      this.mqttWillPayload = ''
      this.mqttWillQoS = ''

      this.step = 'identity'

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    cancel () {
      this.visible = false
      this.step = 'identity'
      this.steps = defaultSteps
      this.$emit('cancel')
    },

    async next () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      return true
    },

    chooseFile (ref) {
      // Strange things... this.$refs[ref] is an array unlike in Templates.vue
      if (isArray(this.$refs[ref])) {
        this.$refs[ref][0].click()
      } else {
        this.$refs[ref].click()
      }
    },

    onFileChange (inputName, files) {
      this[inputName] = files[0]
      this[inputName + 'Name'] = files[0].name
    },

    addEmptyHeader () {
      this.headers.push({ key: '', value: '' })
    },

    removeHeader (i) {
      this.headers.splice(i, 1)

      if (this.headers.length === 0) this.addEmptyHeader()
    },

    async processForm () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      if (!this.isRole.superAdmin) {
        this.customer = this.$store.getters.authenticatedUserCustomer
      }

      const fields = ['name', 'type', 'msgDetailLevel']
      if (this.type === 'HTTP' || this.type === 'WEBSOCKET') {
        fields.push('url')
        // Add user and password only if set
        if (this.user) fields.push('user')
        if (this.password) fields.push('password')
        if (this.type === 'HTTP') fields.push('httpDataUpRoute')
        if (this.type === 'HTTP') fields.push('httpDataDownEventRoute')
        fields.push('headers')
        // Remove empty headers
        remove(this.headers, header => header.key === '' && header.value === '')
      }

      if (this.type === 'MQTT') {
        // Only text fields, number fields are added and converted later
        fields.push(
          'mqttHost', 'mqttTlsEnabled', 'mqttClientId', 'mqttCleanSession',
          'mqttUser', 'mqttPassword',
          'mqttDataUpTopic', 'mqttDataDownEventTopic',
          'mqttWillTopic', 'mqttWillPayload'
        )
      }

      const pushConf = pick(this, fields)
      if (!this.id || this.isRole.superAdmin) { // BUGFIX Mantis 10609: only send customer for creation or patch by superadmin
        pushConf.customer = { id: this.customer.id }
      }

      if (this.type === 'MQTT') {
        // Convert and add number fields
        pushConf.mqttPort = Number(this.mqttPort)
        if (this.mqttConnectTimeout) pushConf.mqttConnectTimeout = Number(this.mqttConnectTimeout)
        if (this.mqttKeepAlive) pushConf.mqttKeepAlive = Number(this.mqttKeepAlive)
        pushConf.mqttQoS = Number(this.mqttQoS)
        if (this.mqttWillQoS) pushConf.mqttWillQoS = Number(this.mqttWillQoS)
      }

      // Remove empty values
      each(pushConf, (value, key) => {
        if (value === '') delete pushConf[key]
      })

      const formData = new FormData()
      formData.append('dto', JSON.stringify(pushConf))
      if (this.tlsCertFile) formData.append('tlsCertFile', this.tlsCertFile)
      if (this.tlsKeyFile) formData.append('tlsKeyFile', this.tlsKeyFile)
      if (this.tlsCaFile) formData.append('tlsCaFile', this.tlsCaFile)

      let error
      if (this.id) [error] = await to(this.$api.patchPushConfiguration(this.pushConfigurationToEdit, formData))
      else [error] = await to(this.$api.createPushConfiguration(this.$store.getters.application, formData))

      if (!error) {
        // Success
        if (this.id) eventBus.$emit('message-success', this.$t('a-pushConfigurationsForm:updateSuccess', { configuration: this.name }))
        else eventBus.$emit('message-success', this.$t('a-pushConfigurationsForm:createSuccess', { configuration: this.name }))
        // Refresh list
        this.$emit('success')
      }

      this.visible = false
      this.step = 'identity'
      // Reset form
      this.reset()
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  .form-row {
    .icon-add,
    .icon-remove {
      font-size: 1rem;
    }

    .icon-remove {
      color: $gray-500;
    }

    &:hover {
      .icon-remove {
        color: $gray-600;
      }
    }
  }
</style>
